import { useQuery } from '@apollo/client';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { ActionRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Action_Bool_Exp,
  Control_Bool_Exp,
  GetActionsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetActionsDocument,
  GetControlsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcActionsToGraphQL } from 'src/hooks/queries/action/useGetActionsRegister';
import { useLabelledFields as useActionLabelledFields } from 'src/pages/actions/useLabelledFields';
import { useLabelledFields as useControlLabelledFields } from 'src/pages/controls/useLabelledFields';
import type { LabelledIdArray } from 'src/rbac/types';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { dateRangeFilter } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dateRangeClickthroughFilter,
  defaultClickthroughFilter,
} from '../dataSourceHelpers';
import {
  getActionOverdueLabel,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';

export type ControlActionRow = TableRecord & {
  Id: string;
  ControlId: string;
  ControlFriendlyId: string;
  ControlTitle: string;
  ControlType: string;
  DesignEffectiveness: string;
  PerformanceEffectiveness: string;
  OverallEffectiveness: string;
  ActionId: string;
  ActionFriendlyId: string;
  ActionTitle: string;
  // Labelled status excluding the overdue fold — overdue is its own column/getter.
  ActionStatus: string;
  ActionPriority: string;
  ActionDateDue: string;
  ActionOwners: LabelledIdArray;
  ActionOverdue: string;
};

export default createDataSource({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Control, Parent_Type_Enum.Action],
  documentNode: GetActionsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.action.register.queryOptions({});
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: ActionRegisterResponse) => mapTrpcActionsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Action_Bool_Exp>(Parent_Type_Enum.Action),
  }),
  useTablePropsHook: (
    data: GetActionsQuery | undefined,
    options: StatefulTableOptions<ControlActionRow>
  ) => {
    const fields = useMemo<TableFields<ControlActionRow>>(
      () => ({
        ControlFriendlyId: simpleField(i18n.t('columns.id')),
        ControlTitle: simpleField(i18n.t('controls.columns.title')),
        ControlType: simpleField(i18n.t('controls.columns.type')),
        DesignEffectiveness: simpleField(
          i18n.t('controls.columns.design_effectiveness')
        ),
        PerformanceEffectiveness: simpleField(
          i18n.t('controls.columns.performance_effectiveness')
        ),
        OverallEffectiveness: simpleField(
          i18n.t('controls.columns.effectiveness')
        ),
        ActionFriendlyId: simpleField(i18n.t('columns.id')),
        ActionTitle: simpleField(i18n.t('actions.columns.title')),
        ActionStatus: simpleField(i18n.t('actions.columns.status')),
        ActionPriority: simpleField(i18n.t('actions.columns.priority')),
        ActionDateDue: simpleField(i18n.t('actions.columns.due_date')),
        ActionOwners: simpleField(i18n.t('actions.columns.owner')),
        ActionOverdue: simpleField(i18n.t('actions.columns.overdue')),
      }),
      []
    );

    const actionsLabelled = useActionLabelledFields(data?.action);
    const { getLabel: getActionStatusLabel } = useRating('action_status');

    const controlWhere = useEntityWhereFilter<Control_Bool_Exp>(
      Parent_Type_Enum.Control
    );
    const { data: controlData } = useQuery(GetControlsDocument, {
      variables: { where: controlWhere },
    });
    const controlsLabelled = useControlLabelledFields(controlData?.control);

    const joinedData = useMemo<ControlActionRow[] | undefined>(() => {
      if (!actionsLabelled || !controlsLabelled) {
        return undefined;
      }

      const controlMap = new Map(controlsLabelled.map((c) => [c.Id, c]));

      return actionsLabelled.flatMap((action) =>
        action.parents
          .filter(
            (p) =>
              p.parent?.ObjectType === Parent_Type_Enum.Control && p.parent.Id
          )
          .map((parent) => {
            const control = controlMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${action.Id}`,
              ControlId: parent.parent!.Id,
              ControlFriendlyId: control?.SequentialIdLabel ?? '',
              ControlTitle: control?.Title ?? parent.control?.Title ?? '',
              ControlType: control?.Type ?? '',
              DesignEffectiveness: control?.DesignEffectivenessLabelled ?? '',
              PerformanceEffectiveness:
                control?.PerformanceEffectivenessLabelled ?? '',
              OverallEffectiveness: control?.OverallEffectivenessLabelled ?? '',
              ActionId: action.Id,
              ActionFriendlyId: action.SequentialIdLabel ?? '',
              ActionTitle: action.Title ?? '',
              ActionStatus: action.Status
                ? (getActionStatusLabel(action.Status) ?? '')
                : '',
              ActionPriority: action.PriorityLabelled ?? '',
              ActionDateDue: action.DateDue ?? '',
              ActionOwners: action.allOwners,
              ActionOverdue: getActionOverdueLabel(action),
            };
          })
      );
    }, [actionsLabelled, controlsLabelled, getActionStatusLabel]);

    return useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('control_action_other'),
      options
    );
  },
  entityNamePlural: 'control_action_other',
  entityNameSingular: 'control_action_one',
  fields: 'controlActions.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'controlFriendlyId',
      name: () => i18n.t('columns.id'),
      categoryGetter: (item: ControlActionRow) =>
        item.ControlFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlFriendlyId'),
    },
    {
      id: 'controlTitle',
      name: () => i18n.t('controls.columns.title'),
      categoryGetter: (item: ControlActionRow) => item.ControlTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlTitle'),
    },
    {
      id: 'controlType',
      name: () => i18n.t('controls.columns.type'),
      categoryGetter: (item: ControlActionRow) => item.ControlType || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlType'),
    },
    {
      id: 'designEffectiveness',
      name: () => i18n.t('controls.columns.design_effectiveness'),
      categoryGetter: (item: ControlActionRow) =>
        item.DesignEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('DesignEffectiveness'),
      ratingColourKey: 'design_effectiveness',
    },
    {
      id: 'performanceEffectiveness',
      name: () => i18n.t('controls.columns.performance_effectiveness'),
      categoryGetter: (item: ControlActionRow) =>
        item.PerformanceEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('PerformanceEffectiveness'),
      ratingColourKey: 'performance_effectiveness',
    },
    {
      id: 'overallEffectiveness',
      name: () => i18n.t('controls.columns.effectiveness'),
      categoryGetter: (item: ControlActionRow) =>
        item.OverallEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('OverallEffectiveness'),
      ratingColourKey: 'effectiveness',
    },
    {
      id: 'actionFriendlyId',
      name: () => i18n.t('columns.id'),
      categoryGetter: (item: ControlActionRow) =>
        item.ActionFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionFriendlyId'),
    },
    {
      id: 'actionTitle',
      name: () => i18n.t('actions.columns.title'),
      categoryGetter: (item: ControlActionRow) => item.ActionTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionTitle'),
    },
    {
      id: 'actionStatus',
      name: () => i18n.t('actions.columns.status'),
      categoryGetter: (item: ControlActionRow) => item.ActionStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionStatus'),
      ratingColourKey: 'action_status',
    },
    {
      id: 'actionPriority',
      name: () => i18n.t('actions.columns.priority'),
      categoryGetter: (item: ControlActionRow) =>
        item.ActionPriority || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionPriority'),
      ratingColourKey: 'priority',
    },
    {
      id: 'actionDateDue',
      name: () => i18n.t('actions.columns.due_date'),
      categoryGetter: (item: ControlActionRow) =>
        item.ActionDateDue ? new Date(item.ActionDateDue) : new Date(0),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: { DateDue: dateRangeFilter(dateRange, precision) },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('ActionDateDue'),
    },
    {
      id: 'actionOwner',
      name: () => i18n.t('actions.columns.owner'),
      categoryGetter: (item: ControlActionRow) =>
        item.ActionOwners.map((owner) => ({
          key: owner.id,
          label: owner.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'ActionOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'actionOverdue',
      name: () => i18n.t('actions.columns.overdue'),
      categoryGetter: (item: ControlActionRow) => item.ActionOverdue,
      clickthroughFilter: defaultClickthroughFilter('ActionOverdue'),
    },
  ],
});
